/**
 * SKU 类型枚举、模板衍生字段（addTemplateType）与添加照片页编辑路由解析。
 * 类型数组集中在本模块；addTemplateType 末尾通过运行时 require(common) 读取环境，避免与 common 静态循环依赖。
 */
import {
  bleedMm,
  borderTypeMap,
  subType_POSTER_ARR,
  sticker_label_layout
} from '@/utils/constant';
import { ROUTE_NAMES } from '@/utils/routeNames';
import * as editUtils from '@/utils/editUtil.js';
import { convertComponentDataToAcrylic } from '@/utils/serviceComponentHole/index.js';
import { getDebugPreviewUrlValue } from '@/utils/debugControlPanel.js';

// skuType: https://www.showdoc.com.cn/xingbai/11558553171262098
export const cardTypeList = [1]; // 证件照
export const pbTypeList = [4]; // 拼版冲印
export const lifeTypeList = [2]; // 生活照
export const nameStickerTypeList = [15]; // 姓名贴
export const pldSkyTypeList = [3, 9, 10, ...nameStickerTypeList]; // 拍立得
export const lifePbTypeList = [11, 12]; //  人生四格
export const keyTypeList = [18]; // 钥匙扣
export const retouchHeadTypeList = []; // 只抠头
export const pasterTypeList = [13, ...keyTypeList, ...retouchHeadTypeList]; // 异型抠图\钥匙扣
export const photoRollTypeList = [14]; // 照片卷
export const isCustomSizeTypeList = [16]; // 自定义尺寸
export const colorPaperTypeList = [19]; // 色纸
export const bajiTypeList = [20]; // 吧唧
export const beidianTypeList = [21]; // 亚克力杯垫
export const templateLayerTypeList = [22]; // 素材:有模板
export const noTemplateLayerTypeList = [23]; // 素材:无模板
export const crystalTypeList = [24]; // 水晶标
export const frontBackLayer = [99]; // 正反面图层
export const stickerTypeList = [25]; // 贴纸
export const templateMutiImage = [26]; // 模板多图
export const templateFreeTypeList = [27]; // 自由模板
export const layerDataSkuTypeList = [
  ...stickerTypeList,
  ...templateMutiImage,
  ...templateFreeTypeList
]; //有layerData
export const layerTypeList = [
  22,
  ...crystalTypeList,
  ...noTemplateLayerTypeList,
  ...frontBackLayer
]; // 模板

export const supportDivideUploadList = [
  // 这里面的路由会直接从orderDetail跳转到addPhoto页面，如果不想要直接到addPhoto，就不把路由加进去
  ...lifeTypeList,
  ...pldSkyTypeList,
  ...lifePbTypeList,
  ...pasterTypeList,
  ...isCustomSizeTypeList,
  ...colorPaperTypeList,
  ...bajiTypeList,
  ...beidianTypeList,
  ...layerTypeList,
  ...layerDataSkuTypeList
]; // 支持拆分上传的类型

let mwxRuntimeCache;

/**
 * 运行期获取 common 模块（首调时 require，避免 skuManage 与 common 顶层互引）。
 * @returns {Object} common 模块命名空间
 */
function getMwxRuntime() {
  if (!mwxRuntimeCache) {
    mwxRuntimeCache = require('@/utils/common.js');
  }
  return mwxRuntimeCache;
}

/**
 * 根据 SKU 模板获取默认出血值。
 * @param {Object} template - SKU 模板
 * @returns {number} 自定义尺寸 SKU 返回 0，否则返回默认出血值
 */
export function getTemplateBleedMm(template) {
  return template && template.isCustomSkuSize ? 0 : bleedMm;
}

/**
 * 判断是否为支持烫色的水晶标阵列。
 * 该产品的本地模板约定是 foilEnabled 表示支持烫色。
 * @param {Object} template - SKU 模板
 * @returns {boolean} 是否进入 layoutEdit
 */
export function isCrystalLayoutFoilSku(template) {
  return !!(
    template &&
    Number(template.skuType) === 25 &&
    template.subType === sticker_label_layout &&
    template.foilEnabled
  );
}

/**
 * 根据 skuType / subType 及运行环境，为订单或商品 template 写入 isXxx 等衍生字段（就地修改）。
 * @param {Object} template - 接口返回的 template 对象
 * @returns {void}
 */
export function addTemplateType(template) {
  let mwx = getMwxRuntime();

  // 证件照
  template.isCard = cardTypeList.indexOf(template.skuType) > -1;
  // 拼版冲印
  template.isPb = pbTypeList.indexOf(template.skuType) > -1;
  // 拍立得
  template.isPld = pldSkyTypeList.indexOf(template.skuType) > -1;
  // 生活照
  template.isLife = lifeTypeList.indexOf(template.skuType) > -1;
  // 人生四格
  template.isLifePb = lifePbTypeList.indexOf(template.skuType) > -1;
  // 支持拆分上传
  template.isSupportDivideUpload =
    supportDivideUploadList.indexOf(template.skuType) > -1;
  // 抠图（异型抠图\钥匙扣\只抠头）
  template.isPaster = pasterTypeList.indexOf(template.skuType) > -1;
  // 钥匙扣
  template.isKeyHole = keyTypeList.indexOf(template.skuType) > -1;
  // 立牌
  template.isLiPai = !!template.slotWidth;
  // 照片卷
  template.isPhotoRoll = photoRollTypeList.indexOf(template.skuType) > -1;
  // 只抠头
  template.isRetouchHead = retouchHeadTypeList.indexOf(template.skuType) > -1;
  // 自定义尺寸
  template.isCustomSize = isCustomSizeTypeList.indexOf(template.skuType) > -1;
  // 色纸
  template.isColorPaper = colorPaperTypeList.indexOf(template.skuType) > -1;
  // 透明色纸\双闪色纸
  template.isPasterColorPaper =
    ['COLOR_ALPHA', 'COLOR_SHAN', 'COLOR_LAMINATED'].indexOf(template.subType) >
    -1;
  // 吧唧
  template.isBaji = bajiTypeList.indexOf(template.skuType) > -1;
  // 双闪吧唧
  template.isPasterBaji = ['BADGE_SHAN'].indexOf(template.subType) > -1;
  // 亚克力杯垫
  template.isBeidian = beidianTypeList.indexOf(template.skuType) > -1;
  // 新方案的sku, 基于 layerInfo
  template.isLayerSku = layerTypeList.indexOf(template.skuType) > -1;
  // 有模板的
  template.isTemplateLayerSku =
    templateLayerTypeList.indexOf(template.skuType) > -1;

  // layerSku: 没有模板选项
  template.isOutlineLayerSku =
    noTemplateLayerTypeList.indexOf(template.skuType) > -1;

  // layerSku: 钥匙扣
  template.isKeyHoleLayerSku =
    template.isOutlineLayerSku && ['HOLE'].indexOf(template.subType) > -1;

  // layerSku: 立牌
  template.isLiPaiLayerSku =
    template.isOutlineLayerSku && ['STAND'].indexOf(template.subType) > -1;

  // 自定义亚克力多组件
  template.isCustomYakeliSku =
    template.isOutlineLayerSku && ['COMPONENT'].indexOf(template.subType) > -1;

  if (mwx.isDev) {
    // template.borderType = borderTypeMap.WHITE_BACKGROUND;
  }

  // borderType 非材料透明时，没有白墨，底色为白色
  template.isNoTranparent =
    template.borderType != borderTypeMap.MATERIAL_TRANSPARENT;

  template.isFrontBackLayer = template.backEnabled; // 是否支持正反面不同图
  // 正反不同图禁用
  if (template.isFrontBackLayer) {
    template.defaultPage = null;
  }

  template.isGeneratePrototypeImage = template.mockupEnabled; // 是否显示样机图

  // 正反图: 多通道图片 逆向和局部UV、白墨
  template.isReverseLayerSku = template.duplexEnabled; // 是否支持反面图

  // 正反图: 无白墨\其他图层
  template.isReverseLayerNoOtherLayerSku =
    template.duplexEnabled && !template.multiChannel; //

  // 是否是水晶标
  template.isCrystalLayerSku = crystalTypeList.indexOf(template.skuType) > -1;

  // 铜版纸海报
  template.isColorPoster =
    subType_POSTER_ARR.indexOf(template.subType) > -1 ||
    template.laminationEnabled;

  // 自由模板
  template.isTemplateFree = template.skuType == 27;
  template.isLayerDataSku =
    layerDataSkuTypeList.indexOf(template.skuType) > -1 ||
    template.isTemplateFree ||
    template.isLayerSku;

  // 是否显示透明网格
  template.isShowTransparentGrid =
    !template.isNoTranparent || template.isCrystalLayerSku;

  // 自由贴纸排版
  template.isAutoLayerPaperSKu = stickerTypeList.indexOf(template.skuType) > -1;

  let dyData = mwx.getDouyinData();
  let wxData = mwx.getWxData();
  let isDouyinApp = dyData && dyData.orderNo; // 是否在抖音小程序中
  let isH5ToMiniApp = !(wxData && wxData.token && wxData.itemId); // h5跳转小程序内下单上传
  //  有itemId说明是小程序内下单上传
  if (isDouyinApp || isH5ToMiniApp) {
    template.isGeneratePrototypeImage = 0;
    template.mockupEnabled = 0;
  }

  if (template.isReverseLayerSku || template.isReverseLayerNoOtherLayerSku) {
    template.isGeneratePrototypeImage = 1;
    template.mockupEnabled = 1;
  }
  let openPreviewUrl = getDebugPreviewUrlValue();
  // 本地样机图开关覆盖接口和环境计算结果
  if (openPreviewUrl === '0' || openPreviewUrl === '1') {
    let previewEnabled = Number(openPreviewUrl);
    template.isGeneratePrototypeImage = previewEnabled;
    template.mockupEnabled = previewEnabled;
  }
  // 自定义尺寸
  template.isCustomSkuSize = template.customize;
  //
  template.isStampingSku = template.foilEnabled;

  if (mwx.isDev) {
    // template.isStampingSku = 1;
    // template.isCustomSkuSize = 1;  // 自定义尺寸
    // template.isCustomYakeliSku = true; // 多组件
    // template.whiteMode = mwx.constant.whiteModeMap.EMPTY;
    //
    // template.isCustomSkuSize = 1;
    // template.isKeyHoleLayerSku = true;
    // template.isLiPaiLayerSku = false;
    // template.subType = 'test';
    // template.isLiPai = false;
    // template.isCustomYakeliSku = true;
    // template.isGeneratePrototypeImage = 1;
    // template.mockupEnabled = 1;
    // template.isReverseLayerNoOtherLayerSku = 1;
    // template.defaultPage = mwx.constant.SkuTemplateDefaultPageMap.segment_body;
  }
  if (template.isAutoLayerPaperSKu) {
    // 贴纸类型： 画布总宽高 = 纸张宽高 + 出血边
    // template.paperWidth = template.paperWidth + template.shadow * 2;
    // template.paperHeight = template.paperHeight + template.shadow * 2;
    template.paperWidthWithBlood = template.paperWidth + template.shadow * 2; // 新增paperWidthWithShadow、paperHeightWithShadow 表示加上1出血后的纸张宽高
    template.paperHeightWithBlood = template.paperHeight + template.shadow * 2;
  }
  if (template.isTemplateFree) {
    // 自由拼版
    template.paperWidthWithBlood = template.paperWidth + template.shadow * 2; // 新增paperWidthWithShadow、paperHeightWithShadow 表示加上1出血后的纸张宽高
    template.paperHeightWithBlood = template.paperHeight + template.shadow * 2;
  }
  /* 需要删除 */
  // template.foilColorList = [
  //   {
  //     foilId: 1,
  //     icon:
  //       'http://elfinbook-test-public.oss-cn-shanghai.aliyuncs.com/img/img/5b11862ce526f492987e7334bad3e37c.jpg',
  //     imageUrl:
  //       'http://elfinbook-test-public.oss-cn-shanghai.aliyuncs.com/img/img/5b11862ce526f492987e7334bad3e37c.jpg',
  //     title: '烫金'
  //   },
  //   {
  //     foilId: 2,
  //     icon:
  //       'http://elfinbook-test-public.oss-cn-shanghai.aliyuncs.com/img/img/eb0b1588a140cb1854f2b13b5f59d535.jpg',
  //     imageUrl:
  //       'http://elfinbook-test-public.oss-cn-shanghai.aliyuncs.com/img/img/eb0b1588a140cb1854f2b13b5f59d535.jpg',
  //     title: '烫银'
  //   }
  // ];
  template.isTemplateMutiImage =
    templateMutiImage.indexOf(template.skuType) > -1; // 模板多图
}

function isEditPaster(template) {
  return (
    template.isPaster ||
    template.isBeidian ||
    ((template.isColorPaper || template.isBaji) &&
      (template.isPasterColorPaper || template.isPasterBaji))
  );
}

/**
 * 添加照片流程中，根据已带 isXxx 的 template 解析编辑页路由 name（与历史 if 链优先级一致）。
 * @param {Object} template - 已执行 addTemplateType 的 template
 * @returns {string} 对应 ROUTE_NAMES；不支持时返回空字符串
 */
export function getAddPhotoEditRouteName(template) {
  // 根据template 模板数据跳转决定addPhoto上传文件之后跳转的页面
  if (template.isTemplateFree || template.skuType == 27) {
    return ROUTE_NAMES.templateFreeEdit;
  }
  if (template.skuType == 2) {
    return ROUTE_NAMES.editLifePhoto;
  }
  if (template.isPld) {
    return ROUTE_NAMES.editPld;
  }
  if (template.isLifePb) {
    return ROUTE_NAMES.selectLifeTemplate;
  }
  if (isEditPaster(template)) {
    return ROUTE_NAMES.editPaster;
  }
  if (template.isCustomSize) {
    return ROUTE_NAMES.customSize;
  }
  if (template.isColorPaper || template.isBaji) {
    if (template.isPasterColorPaper || template.isPasterBaji) {
      return ROUTE_NAMES.editPaster;
    }
    return ROUTE_NAMES.editColorPaper;
  }
  if (template.isLayerSku) {
    return ROUTE_NAMES.edit;
  }
  if (isCrystalLayoutFoilSku(template)) {
    // 水晶标阵列开启烫金 先跳转到编辑页面
    return ROUTE_NAMES.layoutEdit;
  }
  if (template.subType === sticker_label_layout) {
    // 水晶标阵列先进入抠图页面
    return ROUTE_NAMES.stickerRetouchFromAdd;
  }
  if (stickerTypeList.indexOf(template.skuType) > -1) {
    return ROUTE_NAMES.stickerEdit;
  }
  if (templateMutiImage.indexOf(template.skuType) > -1) {
    // 模板多图
    return ROUTE_NAMES.templateMutiImageSelect;
  }
  let errMsg = `getAddPhotoEditRouteName 报错: 当前sku未指定编辑路由页面`;
  if (process.env.NODE_ENV === 'development') {
    alert(errMsg);
  } else {
    console.error(errMsg);
  }
  return '';
}

/**
 * addPhoto 重构：开发环境断言数据转换分支已覆盖。
 * @param {string} message - 错误描述
 * @param {Object} context - 辅助排查上下文
 * @returns {void}
 */
function assertAddPhotoConvertHandled(message, context) {
  let mwx = getMwxRuntime();
  if (!mwx.isDev) {
    return;
  }
  console.error(message, context);
  throw new Error(message);
}

/**
 * addPhoto 重构：获取用于排查转换分支的 template 关键字段。
 * @param {Object} template - SKU 模板
 * @returns {Object} template 关键字段
 */
function getTemplateDebugInfo(template) {
  template = template || {};
  return {
    skuType: template.skuType,
    subType: template.subType,
    isLife: template.isLife,
    isPld: template.isPld,
    isLifePb: template.isLifePb,
    isPaster: template.isPaster,
    isBeidian: template.isBeidian,
    isCustomSize: template.isCustomSize,
    isColorPaper: template.isColorPaper,
    isBaji: template.isBaji,
    isPasterColorPaper: template.isPasterColorPaper,
    isPasterBaji: template.isPasterBaji,
    isLayerSku: template.isLayerSku,
    isLayerDataSku: template.isLayerDataSku
  };
}

/**
 * 判断后端照片项是否为图层编辑数据格式。
 * 该判断只看接口数据形态，不依赖 template 标记，兼容已提交后台后再拉回的数据。
 * @param {Object} item - 后端照片项
 * @returns {boolean} 是否为 imageInfo 图层格式
 */
function isBackendLayerDataItem(item) {
  // 没有 imageInfo 时不能按图层编辑数据还原。
  if (!item || !item.imageInfo || typeof item.imageInfo !== 'object') {
    return false;
  }
  return (
    Array.isArray(item.imageInfo.layers) ||
    !!item.imageInfo.background ||
    item.imageInfo.displayWidth !== undefined ||
    item.imageInfo.displayHeight !== undefined
  );
}

/**
 * 生成前端照片项唯一ID。
 * @param {number} index - 照片项在列表中的索引
 * @returns {string} 前端照片项唯一ID
 */
function createFrontendPhotoItemId(index) {
  return `photo_${Date.now()}_${index}_${Math.random()
    .toString(36)
    .slice(2, 8)}`;
}

/**
 * 归一化前端照片列表项ID，修复二次编辑数据缺少外层 id 导致的异步回调定位错误。
 * @param {Array} list - 前端照片列表
 * @returns {Array} 补齐唯一 id 后的前端照片列表；无需修改时返回原列表引用
 */
export function normalizeFrontendPhotoListItemIds(list) {
  const sourceList = Array.isArray(list) ? list : [];
  const usedIdMap = {};
  let changed = false;
  const normalizedList = sourceList.map((item, index) => {
    const isValidItem = item && typeof item === 'object';
    // 空项不是有效照片项，保持原值交给原有过滤逻辑处理。
    if (!isValidItem) {
      return item;
    }
    const id = item.id;
    const idKey =
      id === undefined || id === null || id === '' ? '' : String(id);
    const shouldReuseId = !!idKey && !usedIdMap[idKey];
    // 已存在且未重复的 id 保持不变，避免影响当前会话内已有上传任务定位。
    if (shouldReuseId) {
      usedIdMap[idKey] = true;
      return item;
    }
    const nextId = createFrontendPhotoItemId(index);
    usedIdMap[String(nextId)] = true;
    changed = true;
    return {
      ...item,
      id: nextId
    };
  });
  return changed ? normalizedList : sourceList;
}

/**
 * addPhoto 重构：创建后端照片项转前端照片项的基础字段。
 * @param {Object} item - 后端照片项
 * @returns {Object} 前端照片项基础字段
 */
function createFrontendListItemBase(item) {
  return {
    ...item,
    status: 'resolve',
    count: item.qty
  };
}

/**
 * addPhoto 重构：转换人生四格后端照片项为前端格式。
 * @param {Object} item - 后端照片项
 * @returns {Object} 前端照片项
 */
function convertFront_LifePb(item) {
  return {
    ...createFrontendListItemBase(item),
    originImageSrc: item.imageUrlList[0]
  };
}

/**
 * addPhoto 重构：转换 layerData 后端照片项为前端格式。
 * @param {Object} item - 后端照片项
 * @param {Object} template - SKU 模板
 * @returns {Object} 前端照片项
 */
function convertFront_LayerDataSku(item, template) {
  return {
    ...createFrontendListItemBase(item),
    ...editUtils.convertBackendToFrontEnd(item, {
      template
    })
  };
}

/**
 * addPhoto 重构：转换普通后端照片项为前端格式。
 * @param {Object} item - 后端照片项
 * @param {Object} template - SKU 模板
 * @returns {Object} 前端照片项
 */
function convertFront_Normal(item, template) {
  let mwx = getMwxRuntime();
  let data = createFrontendListItemBase(item);
  data.originImageSrc = item.imageUrlList[0];
  if (!item.cropInfo && template.isPaster && item.imageInfo) {
    return data;
  }
  data.cropInfo = mwx.convertCropInfo(item.cropInfo, 'fontEnd');
  return data;
}

/**
 * addPhoto 重构：后端照片项转换为前端照片项。
 * @param {Object} options - 参数对象
 * @param {Object} options.item - 后端照片项
 * @param {Object} options.template - SKU 模板
 * @param {boolean} [options.shouldConvertLayerData=true] - 是否允许把 imageInfo 图层数据转换为 layerData
 * @returns {Object} 前端照片项
 */
export function convertBackendItemToFrontendItem(options) {
  let item = options.item;
  let template = options.template || {};
  let shouldConvertLayerData = options.shouldConvertLayerData !== false;
  if (template.isLifePb) {
    return convertFront_LifePb(item);
  }
  // 只有当前项会进入 edit.vue 时，才恢复 imageInfo 图层数据。
  if (
    shouldConvertLayerData &&
    (template.isLayerDataSku || isBackendLayerDataItem(item))
  ) {
    return convertFront_LayerDataSku(item, template);
  }
  if (!item.imageUrlList || !item.imageUrlList.length) {
    assertAddPhotoConvertHandled('addPhoto 后端转前端缺少 imageUrlList', {
      template: getTemplateDebugInfo(template),
      item
    });
  }
  if (!item.cropInfo && !(template.isPaster && item.imageInfo)) {
    assertAddPhotoConvertHandled('addPhoto 后端转前端未匹配到明确分支', {
      template: getTemplateDebugInfo(template),
      item
    });
  }
  return convertFront_Normal(item, template);
}

/**
 * addPhoto 重构：后端列表转换为 addPhoto 前端列表。
 * @param {Object} options - 参数对象
 * @param {Array} options.list - 后端照片列表
 * @param {Object} options.template - SKU 模板
 * @param {boolean|Function} [options.shouldConvertLayerData=true] - 是否允许把 imageInfo 图层数据转换为 layerData
 * @returns {Object} 转换结果，包含 list 和需要回写页面的模板状态
 */
export function convertBackendListToFrontendList(options) {
  let list = options.list || [];
  let template = options.template || {};
  let shouldConvertLayerData = options.shouldConvertLayerData !== false;
  let result = {
    list: [],
    templatePatch: null,
    editRouteName: ''
  };
  result.list = normalizeFrontendPhotoListItemIds(
    list.map((item, index) => {
      let isLayerDataItem = isBackendLayerDataItem(item);
      let shouldConvertCurrentLayerData =
        typeof shouldConvertLayerData === 'function'
          ? shouldConvertLayerData({ item, index })
          : shouldConvertLayerData;
      // 后端已保存 imageInfo 图层数据时，需要切到 edit.vue 的 layerData 二次编辑链路。
      if (shouldConvertCurrentLayerData && isLayerDataItem) {
        result.templatePatch = {
          isLayerSku: true,
          isLayerDataSku: true
        };
        result.editRouteName = ROUTE_NAMES.edit;
      } else if (
        shouldConvertCurrentLayerData &&
        !item.cropInfo &&
        template.isPaster &&
        item.imageInfo
      ) {
        result.templatePatch = {
          isPaster: false,
          isLayerSku: true
        };
        result.editRouteName = ROUTE_NAMES.edit;
      }
      return convertBackendItemToFrontendItem({
        item,
        template,
        shouldConvertLayerData: shouldConvertCurrentLayerData
      });
    })
  );
  return result;
}

/**
 * addPhoto 重构：创建前端照片项转后端照片项的基础字段。
 * @param {Object} item - 前端照片项
 * @param {Object} template - SKU 模板
 * @returns {Object} 后端照片项基础字段
 */
function createBaseSubmitItem(item, template) {
  let mwx = getMwxRuntime();
  let cropInfo = {};
  if (!template.isLifePb && item.cropInfo) {
    // 非人生四格，需要转换裁剪信息
    cropInfo = mwx.convertCropInfo(item.cropInfo, 'toServer');
  }
  if (!template.isCustomSize) {
    // 非自定义尺寸，需要添加纸张尺寸
    cropInfo = {
      ...cropInfo,
      paperWidth: template.paperWidth,
      paperHeight: template.paperHeight
    };
  }
  item.qty = item.count; // 确保count和qty一致
  return {
    qty: item.count,
    imageUrlList: [item.originImageSrc], //用户原图
    cropInfo
  };
}

const customSkuSizeSubmitKeys = [
  'customPaperWidth',
  'customPaperHeight',
  'settingPaperWidth',
  'settingPaperHeight'
];

/**
 * 自定义尺寸 SKU 提交时透传当前图片项的尺寸字段。
 * @param {Object} item - 前端照片项
 * @param {Object} data - 后端照片项
 * @param {Object} template - SKU 模板
 * @returns {Object} 透传尺寸字段后的后端照片项
 */
function appendCustomSkuSizeSubmitFields(item, data, template) {
  // 非自定义尺寸 SKU 不需要透传图片项尺寸字段。
  if (!template.isCustomSkuSize) {
    return data;
  }
  customSkuSizeSubmitKeys.forEach((key) => {
    // 只透传前端图片项上真实存在的尺寸字段。
    if (Object.prototype.hasOwnProperty.call(item, key)) {
      data[key] = item[key];
    }
  });
  return data;
}

/**
 * addPhoto 重构：转换拍立得前端数据为后端数据。
 * @param {Object} options - 参数对象
 * @param {Object} options.item - 前端照片项
 * @param {Object} options.data - 后端照片项基础字段
 * @returns {Object} 后端照片项
 */
function convertBack_Pld(options) {
  let item = options.item;
  let data = options.data;
  data.polaroid = {
    templateId: item.polaroid.templateId,
    imageUrl: item.polaroid.imageUrl,
    rect: item.polaroid.rect,
    imageWidth: item.polaroid.imageWidth,
    imageHeight: item.polaroid.imageHeight
  };
  if (item.fontList && item.fontList.length > 0) {
    data.fontList = item.fontList;
  }
  data.previewUrl = item.previewUrl;
  return data;
}

/**
 * addPhoto 重构：转换生活照前端数据为后端数据。
 * @param {Object} options - 参数对象
 * @param {Object} options.item - 前端照片项
 * @param {Object} options.data - 后端照片项基础字段
 * @param {Object} options.template - SKU 模板
 * @returns {Object} 后端照片项
 */
function convertBack_LifePhoto(options) {
  let item = options.item;
  let data = options.data;
  let template = options.template;
  if (item.fontList && item.fontList.length > 0) {
    data.fontList = item.fontList;
  }
  data.previewUrl = item.previewUrl;
  if (template.isColorPoster) {
    data.extra = item.extra;
  }
  return data;
}

/**
 * 兼容旧 acrylic 直提链路，补齐 slotHoles 第 6 位 radius。
 * @param {Object} acrylic - 前端照片项上的 acrylic 数据
 * @returns {Object} 补齐底座孔圆角后的 acrylic 数据
 */
function normalizeAcrylicSlotHolesRadius(acrylic) {
  // 没有底座孔数组时保持旧 acrylic 数据不变。
  if (!acrylic || !Array.isArray(acrylic.slotHoles)) return acrylic;
  return {
    ...acrylic,
    slotHoles: acrylic.slotHoles.map(function (hole) {
      // 已经包含 radius 或数据结构异常时不重复处理。
      if (!Array.isArray(hole) || hole.length >= 6) return hole;
      // 不完整的历史数据无法判断宽高，保持原值交给既有校验处理。
      if (hole.length < 5) return hole;
      return hole.concat(0);
    })
  };
}

/**
 * addPhoto 重构：转换异形贴纸前端数据为后端数据。
 * @param {Object} options - 参数对象
 * @param {Object} options.item - 前端照片项
 * @param {Object} options.data - 后端照片项基础字段
 * @param {Object} options.template - SKU 模板
 * @param {Function} options.uploadLocalImage - 本地图片上传任务收集函数
 * @param {Array} options.uploadTempImageTask - 原图上传任务数组
 * @param {Array} options.keyHoleUploadImageArr - 钥匙扣预览图上传任务数组
 * @returns {Object} 后端照片项
 */
function convertBack_Paster(options) {
  let item = options.item;
  let data = options.data;
  let template = options.template;
  let uploadLocalImage = options.uploadLocalImage;
  let uploadTempImageTask = options.uploadTempImageTask;
  let keyHoleUploadImageArr = options.keyHoleUploadImageArr;
  data.fileName = item.fileName;
  data.border = item.border;
  data.previewUrl = item.previewUrl;
  data.extra = item.extra;
  data.acrylic = normalizeAcrylicSlotHolesRadius(item.acrylic);

  if (uploadLocalImage) {
    uploadLocalImage({
      imageSrc: item.originImageSrc,
      imageSrcKey: 'originImageSrc',
      promiseArr: uploadTempImageTask
    });
  }

  // 吧唧和色纸的outlineImageSr不是预览图
  let falsePreviewUrl = template.isBaji || template.isColorPaper;
  if (
    item.drawCanvasInfo &&
    item.drawCanvasInfo.outlineImageSrc &&
    !falsePreviewUrl
  ) {
    data.previewUrl = item.drawCanvasInfo.outlineImageSrc;
    if (uploadLocalImage) {
      uploadLocalImage({
        imageSrc: data.previewUrl,
        imageSrcKey: 'previewUrl',
        promiseArr: keyHoleUploadImageArr
      });
    }
  }

  if (template.isKeyHole) {
    if (item.acrylic && item.acrylic.holes) {
      // 有孔位数据才上传
      if (item.acrylic.holes.length) {
        data.acrylic = normalizeAcrylicSlotHolesRadius(item.acrylic);
        delete data.acrylic.previewImageUrl;
      }
    } else {
      data._isError = true;
    }
  }

  if (template.isLiPai) {
    let slotRect = item.acrylic && item.acrylic.slotRect;
    if (slotRect && Array.isArray(slotRect)) {
      let validCount = slotRect.filter(function (slotItem) {
        return slotItem !== null && slotItem !== undefined;
      }).length;
      if (validCount < 6) {
        data._isError = true;
      }
    } else {
      data._isError = true;
    }
  }

  if (
    item.shapeTemplateInfo &&
    item.shapeTemplateInfo.drawCanvasInfo &&
    item.shapeTemplateInfo.shapeOriginImageSrc
  ) {
    // 形状的原图(未裁剪)
    data.imageUrlList = [item.shapeTemplateInfo.shapeOriginImageSrc];
  }
  if (template.isPasterColorPaper) {
    data.extra = item.extra;
  }
  return data;
}

/**
 * addPhoto 重构：转换人生四格前端数据为后端数据。
 * @param {Object} options - 参数对象
 * @param {Object} options.item - 前端照片项
 * @param {Object} options.data - 后端照片项基础字段
 * @returns {Object} 后端照片项
 */
function convertBack_LifePb(options) {
  let item = options.item;
  let data = options.data;
  if (item.puzzleInfo) {
    data.puzzleInfo = item.puzzleInfo;
  }
  return data;
}

/**
 * addPhoto 重构：转换色纸/吧唧前端数据为后端数据。
 * @param {Object} options - 参数对象
 * @param {Object} options.item - 前端照片项
 * @param {Object} options.data - 后端照片项基础字段
 * @returns {Object} 后端照片项
 */
function convertBack_ColorPaper(options) {
  let item = options.item;
  let data = options.data;
  data.extra = item.extra;
  return data;
}

/**
 * addPhoto 重构：转换自定义尺寸前端数据为后端数据。
 * @param {Object} options - 参数对象
 * @param {Object} options.data - 后端照片项基础字段
 * @returns {Object} 后端照片项
 */
function convertBack_CustomSize(options) {
  return options.data;
}

/**
 * 判断当前提交是否为模板多图的正反双面印。
 * @param {Object} template - SKU 模板
 * @returns {boolean} 是否需要按 frontLayers/reverseLayer 分别提交
 */
function isTemplateMutiImageDuplexSubmit(template) {
  return !!(template && template.isTemplateMutiImage && template.duplexEnabled);
}

/**
 * 构造某一面的 layerData，让既有 convertFrontendToBackend 复用同一套 layers 转换。
 * @param {Object} layerData - 当前本地 layerData
 * @param {Array} layers - 指定面需要提交的图层集合
 * @returns {Object} 可传给 convertFrontendToBackend 的 layerData
 */
function createSubmitLayerDataByLayers(layerData, layers) {
  return {
    ...(layerData || {}),
    layers: Array.isArray(layers) ? layers : [],
    reverseLayer: []
  };
}

/**
 * 模板多图双面印提交转换。
 * 正面使用 frontLayers，没有该字段时沿用 layers；反面使用 reverseLayer。
 * @param {Object} layerData - 模板多图本地 layerData
 * @param {Object} template - SKU 模板
 * @returns {{front:Object, back:Object}} 后端 imageInfo/duplex 数据
 */
function convertTemplateMutiImageDuplexLayerData(layerData, template) {
  const frontLayers = Array.isArray(layerData && layerData.frontLayers)
    ? layerData.frontLayers
    : layerData && layerData.layers;
  const reverseLayer = Array.isArray(layerData && layerData.reverseLayer)
    ? layerData.reverseLayer
    : [];
  const frontData = editUtils.convertFrontendToBackend(
    createSubmitLayerDataByLayers(layerData, frontLayers),
    template
  );
  const reverseData = editUtils.convertFrontendToBackend(
    createSubmitLayerDataByLayers(layerData, reverseLayer),
    template
  );

  return {
    front: frontData.front,
    back: reverseData.front
  };
}

/**
 * layerData 提交时，把当前选中的 mask 信息补到后端图层数据。
 * @param {Object} imageInfo - 待提交的 imageInfo 或 duplex
 * @param {Object} layerData - 当前本地 layerData
 */
function appendLayerDataMaskInfo(imageInfo, layerData) {
  if (!imageInfo || !layerData || !layerData.maskId) return;
  imageInfo.maskUrl = layerData.maskUrl;
  imageInfo.maskId = layerData.maskId;
}

/**
 * addPhoto 重构：转换 layerData 前端数据为后端数据。
 * @param {Object} options - 参数对象
 * @param {Object} options.item - 前端照片项
 * @param {Object} options.data - 后端照片项基础字段
 * @param {Object} options.template - SKU 模板
 * @param {number} options.index - 照片项索引
 * @param {Array} options.editLayerImageInfoTasks - imageInfo 上传任务数组
 * @returns {Object} 后端照片项
 */
function convertBack_LayerDataSku(options) {
  let item = options.item;
  let data = options.data;
  let template = options.template;
  let index = options.index;
  let editLayerImageInfoTasks = options.editLayerImageInfoTasks;
  if (item.imageInfo && !item.layerData) {
    editLayerImageInfoTasks.push({
      index: index,
      submitItem: item,
      imageInfo: item.imageInfo
    });
    return item;
  }
  if (!item.layerData) {
    data._isError = true;
    return data;
  }
  let isTemplateMutiImageDuplex = isTemplateMutiImageDuplexSubmit(template);
  let convertedData = isTemplateMutiImageDuplex
    ? convertTemplateMutiImageDuplexLayerData(item.layerData, template)
    : editUtils.convertFrontendToBackend(item.layerData, template);
  data.imageInfo = convertedData.front; // 正面图
  if (template.isReverseLayerSku || isTemplateMutiImageDuplex) {
    data.duplex = convertedData.back; // 反面
  }

  if (template.isKeyHoleLayerSku) {
    data.acrylic = {
      ...(data.acrylic || {}),
      holes: item.layerData.keyPoint
    };
    // 有 componentData 时额外转换多组件格式的钥匙扣孔位数据。
    if (
      item.layerData.componentData &&
      Object.keys(item.layerData.componentData).length
    ) {
      let acrylic = convertComponentDataToAcrylic(item.layerData.componentData);
      data.acrylic = {
        ...(data.acrylic || {}),
        ...acrylic
      };
    }
  } else if (template.isLiPaiLayerSku) {
    let slotRect;
    if (
      item.layerData &&
      item.layerData.socketInfo &&
      item.layerData.socketInfo.slotRect
    ) {
      slotRect = item.layerData.socketInfo.slotRect.slice(0, 6);
      if (slotRect[4] === undefined) {
        let mwx = getMwxRuntime();
        let storeTemplate = mwx.Store.get(mwx.constant.skuTemplate) || {};
        slotRect[4] = storeTemplate.slotWidth;
        slotRect[5] = storeTemplate.slotHeight;
      }
    }
    data.acrylic = {
      ...(data.acrylic || {}),
      slotRect
    };
    if (!slotRect) {
      data._isError = true;
    }
  }
  if (template.isCustomYakeliSku) {
    let componentData = item.layerData.componentData;
    let acrylic = convertComponentDataToAcrylic(componentData);
    data.acrylic = {
      ...(data.acrylic || {}),
      ...acrylic
    };
  }
  // socketOffsetRatio 顶层字段已废弃，插口偏移由 slotRect[0] 和可移动范围反推。
  if (data.acrylic) {
    delete data.acrylic.socketOffsetRatio;
  }
  data.extra = {
    ...(data.extra || {}),
    ...(item.extra || {}),
    mask: item.layerData.backendMaskUrl
  };
  // _foilUrl 仅用于本地重建烫色效果 blobUrl，不提交后台。
  if (data.extra) {
    delete data.extra._foilUrl;
    // 预览图配置只保留在本地 layerData，不再提交后台 extra。
    delete data.extra.watermarkText;
    delete data.extra.watermarkOpacity;
    delete data.extra.mockupImageUrl;
    // 自定义刀线源图为空时不提交，避免清空态污染后端 extra。
    if (!data.extra.originMask) {
      delete data.extra.originMask;
    }
    // 系统形状选中态改由 extra.mask 匹配形状列表恢复，旧字段不再提交。
    delete data.extra.shapeMaskUrl;
  }
  data.border = item.layerData.strokeWidth;
  // imageInfo 只有背景图时也需要进入提交态临时图修复任务。
  if (data.imageInfo) {
    editLayerImageInfoTasks.push({
      index: index,
      submitItem: data,
      imageInfo: data.imageInfo
    });
    if (item.layerData.groupId) {
      data.extra.groupId = item.layerData.groupId;
    }
    appendLayerDataMaskInfo(data.imageInfo, item.layerData);
  }
  if (isTemplateMutiImageDuplex && data.duplex && data.duplex.layers) {
    appendLayerDataMaskInfo(data.duplex, item.layerData);
  }
  data.previewUrl = item.layerData.previewUrl;
  data.imageUrlList = [item.layerData.previewUrl];
  delete data.cropInfo;
  return data;
}

/**
 * addPhoto 重构：根据 template 获取前端转后端处理函数。
 * @param {Object} template - SKU 模板
 * @returns {Function|null} 处理函数
 */
function getConvertBackHandler(template) {
  if (template.isPld) {
    return convertBack_Pld;
  }
  if (template.isLife) {
    return convertBack_LifePhoto;
  }
  if (isEditPaster(template)) {
    return convertBack_Paster;
  }
  if (template.isLifePb) {
    return convertBack_LifePb;
  }
  if (template.isCustomSize) {
    return convertBack_CustomSize;
  }
  if (template.isLayerDataSku) {
    return convertBack_LayerDataSku;
  }
  if (template.isColorPaper || template.isBaji) {
    return convertBack_ColorPaper;
  }
  return null;
}

/**
 * addPhoto 重构：前端照片列表转换为后端提交列表。
 * @param {Object} options - 参数对象
 * @param {Array} options.list - 前端照片列表
 * @param {Object} options.template - SKU 模板
 * @param {Function} options.uploadLocalImage - 本地图片上传任务收集函数
 * @returns {Object} 转换结果，包含 list 和上传任务数组
 */
export function convertFrontendListToBackendList(options) {
  let list = options.list || [];
  let template = options.template || {};
  let uploadTempImageTask = [];
  let keyHoleUploadImageArr = [];
  let editLayerImageInfoTasks = [];
  let handler = getConvertBackHandler(template);
  if (!handler) {
    assertAddPhotoConvertHandled('addPhoto 前端转后端未匹配到转换分支', {
      template: getTemplateDebugInfo(template)
    });
  }
  return {
    list: list.map((item, index) => {
      let data = createBaseSubmitItem(item, template);
      if (!handler) {
        return appendCustomSkuSizeSubmitFields(item, data, template);
      }
      let submitItem = handler({
        item,
        data,
        template,
        index,
        uploadLocalImage: options.uploadLocalImage,
        uploadTempImageTask,
        keyHoleUploadImageArr,
        editLayerImageInfoTasks
      });
      return appendCustomSkuSizeSubmitFields(item, submitItem, template);
    }),
    uploadTempImageTask,
    keyHoleUploadImageArr,
    editLayerImageInfoTasks
  };
}

/**
 * addPhoto 重构：回写提交转换后产生的上传任务结果，并标记错误数据。
 * @param {Object} options - 参数对象
 * @param {Array} options.list - 后端提交列表
 * @param {Object} options.template - SKU 模板
 * @param {Array} options.uploadTempImageTask - 原图上传任务数组
 * @param {Array} options.keyHoleUploadImageArr - 钥匙扣预览图上传任务数组
 * @param {Array} options.editLayerImageInfoTasks - editLayer 类型的 imageInfo 上传任务数组
 * @param {Function} options.uploadImageInfoLayers - imageInfo.layers 上传函数
 * @param {Function} options.uploadSubmitItemLayerImages - 提交项图层上传函数
 * @returns {Promise<Array>} 回写上传结果后的后端提交列表
 */
export async function applySubmitImageTaskResults(options) {
  let mwx = getMwxRuntime();
  let list = options.list || [];
  let template = options.template || {};
  let uploadTempImageTask = options.uploadTempImageTask || [];
  let keyHoleUploadImageArr = options.keyHoleUploadImageArr || [];
  let editLayerImageInfoTasks = options.editLayerImageInfoTasks || [];

  if (
    template.isPaster ||
    template.isBeidian ||
    ((template.isColorPaper || template.isBaji) &&
      (template.isPasterColorPaper || template.isPasterBaji))
  ) {
    if (uploadTempImageTask.length) {
      let arr = await Promise.all(uploadTempImageTask);
      list.forEach((item) => {
        let matchItem = arr.filter(
          (taskItem) => taskItem.originImageSrc == item.imageUrlList[0]
        )[0];
        if (matchItem) {
          if (matchItem.isError) {
            item._isError = true;
          } else {
            item.imageUrlList = [matchItem.imageSrc];
          }
        }
      });
    }

    try {
      if (keyHoleUploadImageArr.length) {
        let arr = await Promise.all(keyHoleUploadImageArr);
        list.forEach((item) => {
          let matchItem = arr.filter(
            (taskItem) => taskItem.previewUrl == item.previewUrl
          )[0];
          if (matchItem) {
            if (matchItem.isError) {
              item._isError = true;
            } else {
              item.previewUrl = matchItem.imageSrc;
            }
          }
        });
      }
    } catch (error) {
      mwx.log.error(error);
    }
  }

  if (
    template.isLayerSku &&
    editLayerImageInfoTasks.length > 0 &&
    (options.uploadSubmitItemLayerImages || options.uploadImageInfoLayers)
  ) {
    try {
      let submitItemLayerUploadTaskMap = {};
      let uploadPromises = editLayerImageInfoTasks.map((task) => {
        let uploadPromise = options.uploadSubmitItemLayerImages
          ? options.uploadSubmitItemLayerImages(
              task.submitItem || list[task.index],
              { urlUploadTaskMap: submitItemLayerUploadTaskMap }
            )
          : options.uploadImageInfoLayers(task.imageInfo);
        return uploadPromise
          .then((updatedSubmitItem) => {
            if (list[task.index]) {
              // 新提交项级上传会就地修复 imageInfo 和 duplex，需要整体回写。
              if (options.uploadSubmitItemLayerImages && updatedSubmitItem) {
                list[task.index] = updatedSubmitItem;
              } else {
                list[task.index].imageInfo = updatedSubmitItem;
              }
            }
            return { index: task.index, submitItem: updatedSubmitItem };
          })
          .catch((error) => {
            mwx.log.error(
              'applySubmitImageTaskResults uploadImageInfoLayers fail:',
              error
            );
            // imageInfo 图层上传失败时标记当前图片，后续 addPhoto 会统一删除。
            if (list[task.index]) {
              list[task.index]._isError = true;
            }
          });
      });
      await Promise.all(uploadPromises);
    } catch (error) {
      mwx.log.error('uploadImageInfoLayers fail:', error);
    }
  }

  return list;
}

/**
 * 判断 SKU 是否需要展示透明网格背景。
 * @param {Object} template - SKU 模板
 * @returns {boolean} 是否展示透明网格背景
 */
export function isTransparentBgSku(template) {
  return (
    template.borderType == borderTypeMap.MATERIAL_TRANSPARENT ||
    template.isLayerSku ||
    templateMutiImage.indexOf(template.skuType) > -1
  );
}
